import { ArrowRightIcon, SparklesIcon } from "@heroicons/react/24/outline";
import { ExternalLinkIcon } from "@radix-ui/react-icons";
import mixpanel from "mixpanel-browser";
import React, { useEffect } from "react";
import {
    EnumSubscriptionInterval,
    EnumUserSubscriptionType,
} from "../../__generated__/graphql";
import { colorsMap600 } from "../../colors-list";
import { useModalContext } from "../../contexts/modals";
import { useToastsContext } from "../../contexts/toasts";
import useIsMobile from "../../hooks/useIsMobile";
import useStores from "../../hooks/useStores";
import {
    Chatnotification1Icon,
    EditIcon,
    FileIcon,
    FilepdfIcon,
    FilesharingIcon,
    Folderadd01Icon,
    GlobeIcon,
} from "../../icon-imports";
import { lightenColor } from "../../utils/lightenColor";
import Pricing from "../Pricing";

type ProPlanWallProps = {
  featureBeingAccessed:
    | "chat"
    | "instant-resources"
    | "image-generator"
    | "workspace";
};

type Feature = {
  name: string;
  description: string;
  icon: React.ReactElement;
  color: string;
  comingSoon?: boolean;
};

function ProPlanWall(props: ProPlanWallProps) {
  const isMobile = useIsMobile();

  const { payments, auth } = useStores();

  const { closeModalAll, addModal } = useModalContext();

  const { addToast } = useToastsContext();

  const PRO_PLAN_CHAT_BENEFITS: Feature[] = [
    {
      name: "Unlimited Conversations",
      description:
        "Send and receive unlimited conversations with Alayna. No message limits per chat.",
      icon: <Chatnotification1Icon />,
      color: "#7915de",
    },
    {
      name: "Unlimited Attachments",
      description:
        "Import unlimited files to your conversation from Computer, Google Drive, Microsoft OneDrive, and more.",
      icon: <FileIcon />,
      color: colorsMap600["pink"],
    },
    {
      name: "Web Research Mode",
      description:
        "Give Alayna the power to browse the web and provide you with the most accurate information & resources from the web.",
      icon: <GlobeIcon />,
      color: colorsMap600["blue"],
    },
    {
      name: "Export as PDF, Google Docs & Forms",
      description:
        "Store your conversation as a PDF, Google Docs, or Google Forms.",
      icon: <FilepdfIcon />,
      color: "#f59e0b",
    },
  ];

  const PRO_PLAN_INSTANT_RESOURCES_BENEFITS: Feature[] = [
    {
      name: "Unlock all Resource types",
      description:
        "Get access to 20+ resource types generated by Alayna in minutes.",
      icon: <FileIcon />,
      color: "#7915de",
    },
    {
      name: "Edit & Rewrite Resources",
      description:
        "Use Alayna's help in modifying resources or make changes yourself.",
      icon: <EditIcon />,
      color: colorsMap600["pink"],
    },
    {
      name: "Export to Google Docs, & Forms",
      description:
        "Export the resources in ready-to-use Google Docs and Forms for classroom use.",
      icon: <FilesharingIcon />,
      color: "#f59e0b",
    },
    {
      name: "Save to My Workspace",
      description:
        "Save your resources to My Workspace and access them anytime.",
      icon: <Folderadd01Icon />,
      color: colorsMap600["blue"],
      comingSoon: true,
    },
  ];

  const PRO_PLAN_IMAGE_GENERATOR_BENEFITS: Feature[] = [
    {
      name: "Unlimited Uses",
      description: "Get unlimited usage for Alayna's Image Generator.",
      icon: <Chatnotification1Icon />,
      color: "#7915de",
    },
    {
      name: "Edit Images",
      description: "Edit the images generated by Alayna.",
      icon: <EditIcon />,
      color: colorsMap600["pink"],
      comingSoon: true,
    },
  ];

  const PRO_PLAN_WORKSPACE_BENEFITS: Feature[] = [
    {
      name: "Download as PDF",
      description: "Download documents from Alayna Drive as PDF.",
      icon: <Folderadd01Icon />,
      color: colorsMap600["blue"],
    },
    {
      name: "Share to LMS",
      description: "Export Alayna's Document to your LMS for easy access.",
      icon: <ExternalLinkIcon />,
      color: "#f59e0b",
      comingSoon: true,
    },
  ];

  const featureLabel =
    props.featureBeingAccessed === "chat"
      ? "Common Teaching Tasks"
      : props.featureBeingAccessed === "instant-resources"
      ? "Magic Materials"
      : props.featureBeingAccessed === "image-generator"
      ? "Create Images"
      : "My Workspace";

  const benefits =
    props.featureBeingAccessed === "chat"
      ? PRO_PLAN_CHAT_BENEFITS
      : props.featureBeingAccessed === "instant-resources"
      ? PRO_PLAN_INSTANT_RESOURCES_BENEFITS
      : props.featureBeingAccessed === "image-generator"
      ? PRO_PLAN_IMAGE_GENERATOR_BENEFITS
      : PRO_PLAN_WORKSPACE_BENEFITS;

  useEffect(() => {
    mixpanel.track("pro-plan-wall-viewed", {
      featureBeingAccessed: props.featureBeingAccessed,
    });
  }, []);

  const redirectToStripe = async (frequency: "monthly" | "annual") => {
    try {
      // Track checkout button click
      mixpanel.track("checkout-button-clicked", {
        plan_frequency: frequency,
        feature_being_accessed: props.featureBeingAccessed,
      });

      // If user is already subscribed, handle upgrade
      if (auth.user?.subscriptionType) {
        const { data, error } = await payments.upgradeSubscription(
          EnumUserSubscriptionType.Copilot,
          frequency === "monthly"
            ? EnumSubscriptionInterval.Monthly
            : EnumSubscriptionInterval.Annually
        );

        if (data) {
          addToast("Subscription updated successfully", { type: "success" });
          auth.refreshSubscriptionUser();
        } else if (error) {
          addToast(error, { type: "error" });
        }
      } else {
        // Create new subscription
        const { data, error } = await payments.createStripeCheckoutSession(
          EnumUserSubscriptionType.Copilot,
          frequency === "monthly"
            ? EnumSubscriptionInterval.Monthly
            : EnumSubscriptionInterval.Annually
        );

        if (error) {
          addToast(error, { type: "error" });
        } else {
          window.location.href = data;
        }
      }
    } catch (error) {
      addToast("An error occurred. Please try again.", { type: "error" });
    }
  };

  const monthlyPrice = import.meta.env.VITE_APP_PRO_PLAN_MONTHLY_PRICE;
  const annualPrice = import.meta.env.VITE_APP_PRO_PLAN_ANNUAL_PRICE;
  const monthlyDiscounted =
    import.meta.env.VITE_APP_PRO_PLAN_MONTHLY_PRICE_DISCOUNTED;
  const annualDiscounted =
    import.meta.env.VITE_APP_PRO_PLAN_ANNUAL_PRICE_DISCOUNTED;
  const limitedTimeOffer = import.meta.env.VITE_PRO_PLAN_LIMITED_TIME_OFFER;

  const annualPerMonth =
    annualPrice && !isNaN(Number(annualPrice))
      ? (Number(annualPrice) / 12).toFixed(2)
      : null;

  return (
    <div className="bg-white">
      <div className="mx-auto max-w-2xl px-6 py-8">
        {/* Header */}
        <div className="mb-10 flex flex-col items-center text-center">
          <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 to-violet-500 shadow-lg shadow-blue-500/25">
            <SparklesIcon className="h-8 w-8 text-white" />
          </div>
          <div className="mb-3 inline-flex items-center gap-2">
            <h1 className="text-3xl font-bold text-gray-900">
              Upgrade to AskAlayna
            </h1>
            <span className="rounded-lg bg-gradient-to-r from-blue-600 to-violet-600 px-2.5 py-1 text-sm font-bold uppercase tracking-wide text-white">
              Pro
            </span>
          </div>
          <p className="max-w-md text-base leading-relaxed text-gray-500">
            Unlock unlimited access and every premium feature when using{" "}
            <span className="font-semibold text-gray-700">{featureLabel}</span>.
          </p>
        </div>

        {/* Features */}
        <div className="mb-10 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {benefits.map((feature: Feature, i) => (
            <div
              key={i}
              className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md"
            >
              <div
                className="flex h-11 w-11 items-center justify-center rounded-xl"
                style={{ backgroundColor: lightenColor(feature.color, 42) }}
              >
                {React.cloneElement(feature.icon, {
                  className: isMobile ? "h-5 w-5" : "h-6 w-6",
                  color: feature.color,
                })}
              </div>
              <div className="mt-4">
                <h2 className="mb-1 flex items-center gap-2 text-base font-semibold text-gray-900">
                  {feature.name}
                  {feature.comingSoon && (
                    <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-blue-600">
                      Soon
                    </span>
                  )}
                </h2>
                <p className="text-sm leading-relaxed text-gray-500">
                  {feature.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Pricing */}
        <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {/* Monthly */}
          <div className="flex flex-col rounded-2xl border border-gray-200 bg-white p-6">
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">
              Monthly
            </span>
            <div className="mb-5 mt-2 flex items-baseline gap-2">
              {monthlyDiscounted ? (
                <>
                  <span className="text-3xl font-bold text-gray-900">
                    ${monthlyDiscounted}
                  </span>
                  <span className="text-lg font-medium text-gray-400 line-through">
                    ${monthlyPrice}
                  </span>
                </>
              ) : (
                <span className="text-3xl font-bold text-gray-900">
                  ${monthlyPrice}
                </span>
              )}
              <span className="text-sm text-gray-500">/mo</span>
            </div>
            {limitedTimeOffer && (
              <span className="mb-4 inline-flex w-fit items-center rounded-full bg-pink-50 px-2.5 py-0.5 text-xs font-semibold text-pink-600">
                Limited time offer
              </span>
            )}
            <button
              type="button"
              onClick={() => redirectToStripe("monthly")}
              className="mt-auto w-full rounded-full border border-gray-200 bg-white px-6 py-3 text-sm font-semibold text-gray-800 transition-all hover:bg-gray-50"
            >
              Get Started
            </button>
          </div>

          {/* Yearly — highlighted */}
          <div className="relative flex flex-col overflow-hidden rounded-2xl bg-gradient-to-br from-blue-600 to-violet-600 p-6 shadow-lg shadow-blue-500/20">
            <div className="absolute right-4 top-4 rounded-full bg-white/20 px-2.5 py-0.5 text-xs font-semibold text-white backdrop-blur">
              Best value
            </div>
            <span className="text-xs font-semibold uppercase tracking-wide text-blue-100">
              Yearly
            </span>
            <div className="mb-1 mt-2 flex items-baseline gap-2">
              {annualDiscounted ? (
                <>
                  <span className="text-3xl font-bold text-white">
                    ${annualDiscounted}
                  </span>
                  <span className="text-lg font-medium text-blue-200 line-through">
                    ${annualPrice}
                  </span>
                </>
              ) : (
                <span className="text-3xl font-bold text-white">
                  ${annualPrice}
                </span>
              )}
              <span className="text-sm text-blue-100">/yr</span>
            </div>
            {annualPerMonth && (
              <p className="mb-5 text-sm text-blue-100">
                Just ${annualPerMonth}/mo, billed annually
              </p>
            )}
            <button
              type="button"
              onClick={() => redirectToStripe("annual")}
              className="group mt-auto flex w-full items-center justify-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-blue-700 shadow-sm transition-all hover:shadow-md"
            >
              Get Started
              <ArrowRightIcon className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </button>
          </div>
        </div>

        {/* View full pricing */}
        <button
          onClick={() => {
            closeModalAll();
            addModal({
              content: (
                <div className="relative h-full overflow-y-scroll px-8">
                  <Pricing />
                </div>
              ),
              size: "lg",
            });
          }}
          className="flex w-full items-center justify-center gap-1.5 py-2 text-sm font-medium text-gray-500 transition-colors hover:text-gray-800"
        >
          Compare all plans & bundles
          <ArrowRightIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export default ProPlanWall;
